import { Link, NavLink, Navigate, useParams } from 'react-router-dom';
import { DOCS, findPage, neighbors } from './toc';
import { renderPage } from './content';

/** /docs shell: sidebar TOC on the left, the active page on the right.
 * URL shape: /docs/<section>/<page>. /docs alone redirects to the first
 * page so the sidebar always has something selected. */
export default function Docs() {
  const { section, page } = useParams<{ section?: string; page?: string }>();

  // Default landing — first page of the first section.
  if (!section || !page) {
    const first = DOCS[0].pages[0];
    return <Navigate to={`/docs/${DOCS[0].slug}/${first.slug}`} replace />;
  }

  const found = findPage(section, page);
  if (!found) return <Navigate to="/docs" replace />;

  const { prev, next } = neighbors(section, page);
  const body = renderPage(section, page);

  return (
    <div className="flex min-h-full">
      {/* Sidebar */}
      <aside className="w-[260px] shrink-0 border-r border-line glass overflow-y-auto py-6 px-3">
        <div className="px-3 mb-4">
          <Link to="/tenants" className="text-[11px] text-dim hover:text-ink transition">
            ← Back to app
          </Link>
        </div>
        <nav className="space-y-5">
          {DOCS.map(s => (
            <div key={s.slug}>
              <div className="px-3 text-[10px] uppercase tracking-wider text-dim/80 mb-1.5">
                {s.title}
              </div>
              <ul className="space-y-px">
                {s.pages.map(p => (
                  <li key={p.slug}>
                    <NavLink
                      to={`/docs/${s.slug}/${p.slug}`}
                      className={({ isActive }) =>
                        `block px-3 py-1.5 text-xs rounded-md transition ${
                          isActive
                            ? 'bg-accent/15 text-ink shadow-[inset_0_0_0_1px_rgba(139,92,246,0.25)]'
                            : 'text-dim hover:text-ink hover:bg-white/[0.03]'
                        }`
                      }
                    >
                      {p.title}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
      </aside>

      {/* Content */}
      <main className="flex-1 overflow-auto">
        <article className="max-w-3xl mx-auto px-8 py-10">
          {/* Breadcrumb */}
          <nav className="text-[11px] text-dim mb-4 flex items-center gap-1.5">
            <Link to="/docs" className="hover:text-ink transition">Docs</Link>
            <span className="opacity-50">/</span>
            <span>{found.sectionTitle}</span>
            <span className="opacity-50">/</span>
            <span className="text-ink">{found.page.title}</span>
          </nav>

          {/* Heading */}
          <h1 className="text-3xl font-semibold tracking-tight mb-2">{found.page.title}</h1>
          {found.page.blurb && (
            <p className="text-sm text-dim mb-8">{found.page.blurb}</p>
          )}
          {!found.page.blurb && <div className="mb-6" />}

          {/* Body */}
          <div className="docs-body space-y-4">{body}</div>

          {/* Prev / next */}
          <div className="mt-12 pt-6 border-t border-line/60 flex items-center justify-between gap-3">
            {prev ? (
              <Link to={prev.href} className="card p-3 px-4 hover:bg-white/[0.03] transition flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-dim">Previous</div>
                <div className="text-sm text-ink truncate">← {prev.page.title}</div>
              </Link>
            ) : <div className="flex-1" />}
            {next ? (
              <Link to={next.href} className="card p-3 px-4 hover:bg-white/[0.03] transition flex-1 min-w-0 text-right">
                <div className="text-[10px] uppercase tracking-wider text-dim">Next</div>
                <div className="text-sm text-ink truncate">{next.page.title} →</div>
              </Link>
            ) : <div className="flex-1" />}
          </div>
        </article>
      </main>
    </div>
  );
}


/** Small helpers exported for use inside content.tsx. */

export function H2({ children, id }: { children: React.ReactNode; id?: string }) {
  return (
    <h2 id={id} className="text-lg font-semibold text-ink mt-8 mb-2 scroll-mt-8">{children}</h2>
  );
}

export function H3({ children, id }: { children: React.ReactNode; id?: string }) {
  return (
    <h3 id={id} className="text-base font-semibold text-ink mt-6 mb-2 scroll-mt-8">{children}</h3>
  );
}

export function P({ children }: { children: React.ReactNode }) {
  return <p className="text-sm leading-relaxed text-ink">{children}</p>;
}

export function UL({ children }: { children: React.ReactNode }) {
  return <ul className="list-disc pl-5 space-y-1 text-sm text-ink leading-relaxed">{children}</ul>;
}

export function OL({ children }: { children: React.ReactNode }) {
  return <ol className="list-decimal pl-5 space-y-1.5 text-sm text-ink leading-relaxed">{children}</ol>;
}

export function Code({ children }: { children: React.ReactNode }) {
  return <code className="font-mono text-[12px] text-ink bg-white/[0.04] px-1.5 py-0.5 rounded">{children}</code>;
}

export function Pre({ children }: { children: React.ReactNode }) {
  return (
    <pre className="font-mono text-[12px] text-ink bg-bg/50 border border-line rounded-md p-3 overflow-x-auto whitespace-pre">
      {children}
    </pre>
  );
}

export function Callout({
  kind = 'note', children,
}: { kind?: 'note' | 'warn' | 'tip'; children: React.ReactNode }) {
  const cls =
    kind === 'warn' ? 'border-warn/40 bg-warn/5'
  : kind === 'tip'  ? 'border-ok/40 bg-ok/5'
  : 'border-accent/40 bg-accent/5';
  const label = kind === 'warn' ? '⚠ Warning' : kind === 'tip' ? '💡 Tip' : 'ℹ Note';
  return (
    <div className={`border-l-2 ${cls} pl-4 py-2 my-3`}>
      <div className="text-[10px] uppercase tracking-wider text-dim mb-1">{label}</div>
      <div className="text-sm leading-relaxed text-ink">{children}</div>
    </div>
  );
}

export function AppLink({ to, children }: { to: string; children: React.ReactNode }) {
  return <Link to={to} className="text-accent hover:underline">{children}</Link>;
}

export function Ext({ href, children }: { href: string; children: React.ReactNode }) {
  return <a href={href} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">{children}</a>;
}
